import {
  LANDING_PARTNER_NAME_MAX_LENGTH,
  type LandingPartnerFields,
  landingPartnerFieldsSchema,
} from '@repo/api-contracts';
import { type FormEvent, type ReactElement, useState } from 'react';
import { z } from 'zod';
import { firstErrorPerField } from '@/shared/lib';
import { Button, FileDropField, Input, Label } from '@/shared/ui';

const ACCEPT = 'image/jpeg,image/png,image/webp';
const FIELD_KEYS = ['name'] as const;
const EMPTY_FIELDS: LandingPartnerFields = { name: '' };

type FieldErrors = Partial<Record<keyof LandingPartnerFields, string>>;

interface LandingPartnerFormModalProps {
  /** create 는 로고와 기업명을, edit 는 기업명만 받는다. */
  readonly mode: 'create' | 'edit';
  /** edit 의 기존 기업명. create 는 null. */
  readonly initialFields: LandingPartnerFields | null;
  readonly isPending: boolean;
  readonly errorMessage: string | null;
  readonly onSubmit: (fields: LandingPartnerFields, file: File | null) => void;
  readonly onClose: () => void;
}

/** 기업 로고 등록·수정 모달 — 서버와 같은 계약 스키마로 먼저 검사해 칸별 오류를 보여 준다. */
export const LandingPartnerFormModal = ({
  mode,
  initialFields,
  isPending,
  errorMessage,
  onSubmit,
  onClose,
}: LandingPartnerFormModalProps): ReactElement => {
  const [fields, setFields] = useState<LandingPartnerFields>(initialFields ?? EMPTY_FIELDS);
  const [file, setFile] = useState<File | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [fileError, setFileError] = useState<string | null>(null);
  const title = mode === 'create' ? '기업 로고 추가' : '기업 로고 수정';

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const parsed = landingPartnerFieldsSchema.safeParse(fields);
    const nextFileError = mode === 'create' && file === null ? '로고 파일을 골라 주세요.' : null;

    setFieldErrors(
      parsed.success
        ? {}
        : firstErrorPerField(z.flattenError(parsed.error).fieldErrors, FIELD_KEYS),
    );
    setFileError(nextFileError);

    if (parsed.success && nextFileError === null) {
      onSubmit(parsed.data, file);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="w-full max-w-md rounded-lg border bg-background p-5"
      >
        <p className="font-medium">{title}</p>

        {errorMessage !== null ? (
          <p role="alert" className="mt-2 text-sm text-destructive">
            {errorMessage}
          </p>
        ) : null}

        <form onSubmit={handleSubmit} noValidate className="mt-4 flex flex-col gap-3">
          {mode === 'create' ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="partner-form-logo">로고</Label>
              <FileDropField
                id="partner-form-logo"
                accept={ACCEPT}
                buttonLabel="로고 선택"
                fileName={file?.name ?? null}
                aria-invalid={fileError !== null}
                onFiles={([next]) => setFile(next ?? null)}
              />
              <p className="text-xs text-muted-foreground">
                배경이 투명한 PNG 권장. HOME 에서는 높이 48px 안에 맞춰 보여 줍니다.
              </p>
              {fileError !== null ? <p className="text-xs text-destructive">{fileError}</p> : null}
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="partner-form-name">기업명</Label>
            <Input
              id="partner-form-name"
              value={fields.name}
              maxLength={LANDING_PARTNER_NAME_MAX_LENGTH}
              placeholder="예: 삼성전자"
              onChange={(event) => setFields({ name: event.target.value })}
              aria-invalid={fieldErrors.name !== undefined}
            />
            <p className="text-xs text-muted-foreground">
              로고를 읽지 못하는 환경에서 대신 읽히는 이름입니다.
            </p>
            {fieldErrors.name !== undefined ? (
              <p className="text-xs text-destructive">{fieldErrors.name}</p>
            ) : null}
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              취소
            </Button>
            <Button type="submit" size="sm" isLoading={isPending}>
              저장
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
